import { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import {
  Text, TextInput, Group, Button, Alert, AspectRatio, Box, Slider, Loader, Stack,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { IconCamera, IconAlertCircle } from '@tabler/icons-react';
import { MediaStatusType } from '@lib2/model/media/MediaTypes';
import { MediaPlayerLinker } from '@lib2/model/player/PlayerLinker';
import { useResellerBrand } from '@/shared/hooks/useResellerBrand';
import { usePlayers } from '@/features/players/usePlayers';
import { MediaUtils } from '../MediaUtils';
import type { VideoViewData } from '@/lib/types/Media';
import type IMediaView from '@lib2/model/media/IMediaView';

interface CaptureFrameFormProps {
  video: VideoViewData;
  onCapture: (file: File) => void;
  onClose: () => void;
}

export function CaptureFrameForm({ video, onCapture, onClose }: CaptureFrameFormProps) {
  const { defaultPlayerId } = usePlayers();
  const isReseller = useResellerBrand();

  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);

  const [isVideoLoaded, setIsVideoLoaded] = useState(false);
  const [isVideoError, setIsVideoError] = useState(false);
  const [isSeeking, setIsSeeking] = useState(false);
  const [isCapturing, setIsCapturing] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [sliderValue, setSliderValue] = useState(0);
  const [duration, setDuration] = useState(0);
  const [jumpTimecode, setJumpTimecode] = useState('');

  // Construct CDN video URL using same pattern as VideoPreview
  const videoUrl = useMemo(() => {
    if (!defaultPlayerId) return null;
    if (video.media.mediaStatusType !== MediaStatusType.Ready) return null;
    const mediaView = { data: video } as unknown as IMediaView;
    const linker = new MediaPlayerLinker(defaultPlayerId, mediaView, isReseller);
    return linker.getFileUrl(false);
  }, [defaultPlayerId, video, isReseller]);

  // Draw current video frame onto canvas
  const drawFrame = useCallback(() => {
    const vid = videoRef.current;
    const canvas = canvasRef.current;
    if (!vid || !canvas) return;

    // Size canvas to native video resolution for full-quality capture
    canvas.width = vid.videoWidth;
    canvas.height = vid.videoHeight;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.drawImage(vid, 0, 0, canvas.width, canvas.height);
  }, []);

  const handleVideoLoaded = useCallback(() => {
    const vid = videoRef.current;
    if (!vid) return;
    setDuration(vid.duration);
    // Seek to 10% (capped at 30s) to skip black/intro frames
    vid.currentTime = Math.min(vid.duration * 0.1, 30);
  }, []);

  const handleVideoError = useCallback(() => {
    setIsVideoError(true);
  }, []);

  const handleSeeked = useCallback(() => {
    const vid = videoRef.current;
    if (!vid) return;

    drawFrame();

    const time = vid.currentTime;
    setCurrentTime(time);
    setSliderValue(time);
    setIsSeeking(false);
    if (!isVideoLoaded) setIsVideoLoaded(true);
  }, [drawFrame, isVideoLoaded]);

  // Slider: responsive drag, seek on release
  function handleSliderChange(value: number) {
    setSliderValue(value);
  }

  function handleSliderChangeEnd(value: number) {
    if (!videoRef.current) return;
    setIsSeeking(true);
    videoRef.current.currentTime = value;
  }

  // Manual timecode jump
  function handleJumpTimecode() {
    const seconds = MediaUtils.parseTimecode(jumpTimecode);
    if (seconds == null || seconds < 0 || seconds > duration) return;
    if (videoRef.current) {
      setIsSeeking(true);
      videoRef.current.currentTime = seconds;
    }
  }

  // Capture frame as JPEG
  function handleCapture() {
    const canvas = canvasRef.current;
    if (!canvas) return;
    setIsCapturing(true);

    canvas.toBlob(
      (blob) => {
        setIsCapturing(false);
        if (!blob) {
          notifications.show({
            title: 'Capture failed',
            message: 'Could not capture frame from video.',
            color: 'red',
          });
          return;
        }
        const filename = `poster-frame-${video.media.mediaId}-${Math.floor(currentTime)}.webp`;
        const file = new File([blob], filename, { type: 'image/webp' });
        onCapture(file);
        onClose();
      },
      'image/webp',
      0.90
    );
  }

  // Cleanup: release video resources on unmount
  useEffect(() => {
    return () => {
      const vid = videoRef.current;
      if (vid) {
        vid.pause();
        vid.removeAttribute('src');
        vid.load();
      }
    };
  }, []);

  const isLoading = !isVideoLoaded && !isVideoError;
  const canCapture = isVideoLoaded && !isSeeking && !isCapturing && !isVideoError;

  return (
    <Stack gap="md">
      <Text size="sm" c="dimmed">
        Scrub through the video to find the frame you want, then capture it as your poster image.
      </Text>

      {/* Canvas preview */}
      <AspectRatio ratio={16 / 9} bg="gray.1" mb="md" style={{ borderRadius: 'var(--mantine-radius-sm)', overflow: 'hidden' }}>
        <Box pos="relative" w="100%" h="100%">
          <canvas
            ref={canvasRef}
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'contain',
              display: isVideoLoaded ? 'block' : 'none',
            }}
          />

          {/* Loading overlay */}
          {isLoading && (
            <Box
              pos="absolute"
              top={0}
              left={0}
              w="100%"
              h="100%"
              style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}
            >
              <Stack align="center" gap="xs">
                <Loader size="sm" />
                <Text size="sm" c="dimmed">Loading video...</Text>
              </Stack>
            </Box>
          )}

          {/* Error state */}
          {isVideoError && (
            <Box
              pos="absolute"
              top={0}
              left={0}
              w="100%"
              h="100%"
              p="md"
              style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}
            >
              <Alert icon={<IconAlertCircle size={16} />} color="red" title="Video unavailable">
                Could not load the video. You can still upload a poster image directly.
              </Alert>
            </Box>
          )}
        </Box>
      </AspectRatio>

      {/* Seek controls — only shown when video is loaded */}
      {isVideoLoaded && (
        <>
          <Slider
            min={0}
            max={duration}
            step={0.1}
            value={sliderValue}
            onChange={handleSliderChange}
            onChangeEnd={handleSliderChangeEnd}
            label={(v) => MediaUtils.formatTimecode(Math.floor(v))}
            mb="xs"
          />

          <Group justify="space-between" mb="md">
            <Text size="sm" c="dimmed">{MediaUtils.formatTimecode(Math.floor(currentTime))}</Text>
            <Text size="sm" c="dimmed">{MediaUtils.formatTimecode(Math.floor(duration))}</Text>
          </Group>

          <TextInput
            label="Jump to timestamp"
            placeholder="00:00:00"
            description="Press Enter to jump (MM:SS or HH:MM:SS)"
            value={jumpTimecode}
            onChange={(e) => setJumpTimecode(e.currentTarget.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') handleJumpTimecode();
            }}
            mb="md"
            maw={200}
          />
        </>
      )}

      {/* Hidden video element */}
      <video
        ref={videoRef}
        src={videoUrl ?? undefined}
        crossOrigin="anonymous"
        preload="metadata"
        style={{ display: 'none' }}
        onLoadedMetadata={handleVideoLoaded}
        onError={handleVideoError}
        onSeeked={handleSeeked}
      />

      {/* Action buttons */}
      <Group justify="flex-end" gap="sm">
        <Button variant="default" onClick={onClose}>
          Cancel
        </Button>
        <Button
          leftSection={<IconCamera size={16} />}
          onClick={handleCapture}
          disabled={!canCapture}
          loading={isCapturing}
        >
          Use This Frame
        </Button>
      </Group>
    </Stack>
  );
}
